import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { observer } from 'mobx-react-lite';
import { Field } from '../../../shared/ui';
import { Button } from '../../../shared/ui';
import { Subtitle1 } from '../../../shared/ui';
import { colors } from '../../../shared/styles/colors';
import {userStore} from '../../../entities/user/model/user-store';

const FormWrapper = styled.form`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 36px;
    width: 100%;
    max-width: 410px;
    align-items: center;
`;

const ErrorMessage = styled.div`
    color: ${colors.errorState};
    font-size: 12px;
    margin-top: -8px;
    margin-bottom: 4px;
`;

// Schema validation for the password change form
const changePasswordSchema = z.object({
  oldPassword: z.string().min(1, 'Старий пароль обов\'язковий'),
  newPassword1: z.string()
    .min(1, 'Новий пароль обов\'язковий')
    .min(8, 'Пароль повинен містити мінімум 8 символів'),
  newPassword2: z.string().min(1, 'Підтвердження паролю обов\'язкове'),
}).refine((data) => data.newPassword1 === data.newPassword2, {
  message: 'Паролі не співпадають',
  path: ['newPassword2'],
});

// Form data type based on the schema
type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>;

export const ChangePasswordForm = observer(() => {
  const [serverError, setServerError] = useState('');
  const [isOldPasswordChecked, setIsOldPasswordChecked] = useState(false);

  const methods = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      oldPassword: '',
      newPassword1: '',
      newPassword2: ''
    }
  });

  const {handleSubmit, setValue, formState, watch, trigger, getValues} = methods;
  const oldPassword = watch('oldPassword');

  // Reset error states when component unmounts
  useEffect(() => {
    return () => {
      userStore.resetAuthErrors();
    };
  }, []);

  // Reset old password checked state when old password changes
  useEffect(() => {
    setIsOldPasswordChecked(false);
  }, [oldPassword]);

  const checkOldPassword = async () => {
    setServerError('');

    if (!oldPassword || formState.errors.oldPassword) {
      await trigger('oldPassword');
      return;
    }

    try {
      const isValid = await userStore.checkOldPassword(oldPassword);

      if (isValid) {
        setIsOldPasswordChecked(true);
      } else {
        setServerError('Невірний старий пароль');
      }

      return isValid;
    } catch (err) {
      setServerError('Помилка перевірки паролю');
      return false;
    }
  };

  const onSubmit = handleSubmit(async (data: ChangePasswordFormValues) => {
    setServerError('');

    // If old password not yet checked, check it first
    if (!isOldPasswordChecked) {
      const isValid = await checkOldPassword();
      if (!isValid) return;
    }

    try {
      const success = await userStore.changePassword(data.newPassword1, data.newPassword2);

      if (success) {
        // Redirect or show success message

      } else if (userStore.authError) {
        setServerError(userStore.authError);
      }
    } catch (err) {
      setServerError('Сталася помилка під час зміни паролю');
    }
  });

  return (
    <FormProvider {...methods}>
      <FormWrapper onSubmit={onSubmit}>
        <Subtitle1 color={colors.accentPrimary}>Змінити пароль</Subtitle1>

        <div className="flex flex-col gap-4 flex-1 w-full">
          <Field
            label="Старий пароль"
            name="oldPassword"
            type="password"
            height={'40px'}
            placeholder="Введіть старий пароль *"
            onChange={(e) => setValue('oldPassword', e.target.value, {shouldValidate: true})}
            error={formState.errors.oldPassword?.message}
            isSuccessful={isOldPasswordChecked}
          />
          {formState.errors.oldPassword && (
            <ErrorMessage>{formState.errors.oldPassword.message}</ErrorMessage>
          )}

          <Field
            label="Новий пароль"
            name="newPassword1"
            type="password"
            height={'40px'}
            placeholder="Введіть новий пароль *"
            onChange={(e) => setValue('newPassword1', e.target.value, {shouldValidate: true})}
            error={formState.errors.newPassword1?.message}
            isSuccessful={formState.isValid && formState.dirtyFields.newPassword1}
          />
          {formState.errors.newPassword1 && (
            <ErrorMessage>{formState.errors.newPassword1.message}</ErrorMessage>
          )}

          <Field
            label="Повторіть новий пароль"
            name="newPassword2"
            type="password"
            height={'40px'}
            placeholder="Повторіть новий пароль *"
            onChange={(e) => setValue('newPassword2', e.target.value, {shouldValidate: true})}
            error={formState.errors.newPassword2?.message}
            isSuccessful={formState.isValid && formState.dirtyFields.newPassword2}
          />
          {formState.errors.newPassword2 && (
            <ErrorMessage>{formState.errors.newPassword2.message}</ErrorMessage>
          )}

          {serverError && <ErrorMessage>{serverError}</ErrorMessage>}
        </div>

        <div className="flex flex-1 items-end w-full">
          {userStore.authStatus === 'pending' ? (
            <Button type="button" variant="primary" fullWidth disabled>
              Завантаження...
            </Button>
          ) : (
            <Button
              size="large"
              type="submit"
              variant="primary"
              fullWidth
              disabled={!getValues('oldPassword') || !getValues('newPassword1') || !getValues('newPassword2')}
            >
              Змінити
            </Button>

          )}
        </div>
      </FormWrapper>
    </FormProvider>
  );
});
